import {
  createApi,
  fetchBaseQuery,
  BaseQueryFn,
  FetchArgs,
  FetchBaseQueryError,
} from "@reduxjs/toolkit/query/react";
import { RootState } from "../store";
import { logout } from "../store/slices/authSlice";
import { toast } from "sonner";
import { getMessageFromApiErrorBody } from "../lib/helpers";

import { getPermissions, hasPermission, checkIsAdmin } from "../utils/auth";

const baseQuery = fetchBaseQuery({
  baseUrl: import.meta.env.VITE_API_BASE_URL || "https://api-staging.fairchildinstitute.com/api/v1/institution",
  prepareHeaders: (headers, { getState }) => {
    const token = (getState() as RootState).auth.token;

    if (token) {
      headers.set("authorization", `Bearer ${token}`);
    }

    headers.set("Content-Type", "application/json");
    return headers;
  },
});

// Error handling wrapper
const baseQueryWithErrorHandling: BaseQueryFn<
  string | FetchArgs,
  unknown,
  FetchBaseQueryError,
  { skipToast?: boolean; requiredPermission?: string | string[] }
> = async (args, api, extraOptions) => {
  const state = api.getState() as RootState;
  const user = state.auth.user;
  const isAdmin = checkIsAdmin(user);

  // Proactive permission check
  let required = extraOptions?.requiredPermission;

  // FAIL-SAFE: Auto-assign system:config for sensitive paths if not specified
  const url = typeof args === 'string' ? args : args.url;
  if (!required && url?.includes('/staff/institution/settings')) {
    required = 'system:config';
  }

  if (required && !isAdmin) {
    const userPermissions = getPermissions(user as any);

    let isAllowed = false;
    if (Array.isArray(required)) {
      isAllowed = required.some((p) => userPermissions.includes(p));
    } else {
      isAllowed = userPermissions.includes(required);
    }

    if (!isAllowed) {
      // Return a 403 error locally to prevent the network request from firing
      return {
        error: {
          status: 403,
          data: {
            message: "Frontend Guard: Access Denied",
            detail: `Missing required permission: ${required}`
          },
        } as FetchBaseQueryError,
      };
    }
  }

  const result = await baseQuery(args, api, extraOptions);

  // Handle errors
  if (result.error) {
    const { status, data } = result.error;
    const skipToast = extraOptions?.skipToast === true;

    const extractErrorMessage = (errorData: unknown): string =>
      getMessageFromApiErrorBody(errorData) ?? "An error occurred";

    // Standard styling options for all global toasts
    const toastOptions = {
      duration: 5000,
    };

    // Handle authentication errors (401 only) - NEVER skip these
    if (status === 401) {
      const isInitializing = (window as Window & { __AUTH_INITIALIZING__?: boolean }).__AUTH_INITIALIZING__ === true;

      if (!isInitializing) {
        const requestUrl = typeof args === "string" ? args : args.url;
        // Credential / sign-in attempts return 401 for bad credentials — not a session expiry.
        const isAuthAttempt =
          /^\/auth\/login(?:\/|$)/.test(requestUrl) ||
          requestUrl.startsWith("/auth/register") ||
          requestUrl.startsWith("/auth/2fa/") ||
          /^\/oauth\/[^/]+\/auth-url/.test(requestUrl);

        if (isAuthAttempt) {
          // Let the SignInPage / auth-context handle displaying the error
        } else {
          api.dispatch(logout());
          toast.error("Session Expired", {
            ...toastOptions,
            description: "Please log in again to continue.",
          });

          if (window.location.pathname !== "/auth/login") {
            window.location.href = "/auth/login";
          }
        }
      }
      return result;
    }

    // If skipToast is true, don't show any remaining toasts
    if (skipToast) {
      return result;
    }

    // Handle network errors
    if (status === "FETCH_ERROR" || status === "PARSING_ERROR") {
      toast.error("Network Error", {
        ...toastOptions,
        description: "Unable to connect to the server. Please check your internet connection.",
      });
      return result;
    }

    // Handle server errors (500+)
    if (status && typeof status === "number" && status >= 500) {
      toast.error("Server Error", {
        ...toastOptions,
        description: "Something went wrong on our end. Please try again later.",
      });
      return result;
    }

    // Handle client errors (400, 403, 404, etc.)
    if (status && typeof status === "number" && status >= 400 && status < 500) {
      const errorMessage = extractErrorMessage(data);

      // For 400 (Bad Request), 403 (Forbidden), 404 (Not Found), 409 (Conflict), etc.
      const title =
        status === 400 ? "Invalid Request" :
          status === 403 ? "Access Denied" :
            status === 404 ? "Not Found" :
              status === 422 ? "Validation Error" :
                status === 409 ? "Conflict Error" :
                  "Request Error";

      toast.error(title, {
        ...toastOptions,
        description: errorMessage,
      });
    }
  }

  return result;
};

export const baseApi = createApi({
  reducerPath: "baseApi",
  baseQuery: baseQueryWithErrorHandling,
  tagTypes: [
    "EntityLabel",
    "User",
    "OAuth",
    "Course",
    "Lesson",
    "Enrollment",
    "Department",
    "Announcement",
    "AnnouncementCategory",
    "Module",
    "Category",
    "Payment",
    "CourseQuote",
    "PaymentPlan",
    "Product",
    "Discount",
    "Role",
    "Institution",
    "Auth",
    "Quiz",
    "Progress",
    "Instructor",
    "StaffManagement",
    "Student",
    "StudentApplication",
    "FinancialAidApplication",
    "ApplicationWaitlist",
    "Task",
    "TaskSubmission",
    "Ticket",
    "Badge",
    "Programme",
    "ProgrammeDivision",
    "Class",
    "ClassAsset",
    "LessonMaterial",
    "AcademicSession",
    "AuditLog",
    "CourseRevision",
    "AttendanceSession",
    "AttendanceRecord",
    "AttendancePolicy",
    "AttendancePolicyHistory",
    "CourseAttendanceSummary",
    "CourseAttendanceReport",
    "SessionAttendanceReport",
    "Analytics",
    "Dashboard",
    "ChatRoom",
    "ChatMessage",
    "ChatManagement",
    "BlockedUser",
    "FlaggedMessage",
    "ArchivedChat",
    "Grade",
    "CourseFeedbackQuestion",
    "CourseSection",
    "Event",
    "Result",
    "Certificate",
    "Notification",
    "Security",
    "UserSession",
    "EmailTemplate",
    "Reports",
    "AuthCarousel",
    "KnowledgeBaseCategory",
    "KnowledgeBaseArticle",
    "Impersonation",
  ],
  endpoints: () => ({}),
});

export default baseApi;
